import { MaxDropLimits } from "helpers/types";
import { useStrategyReportValue } from "./useReportStorage";

const MAX_DROP_LIMITS_KEY = "max_drop_limits_strategy";
const DEFAULT_MAX_DROP = 20;

// The axis bounds when NEITHER drop is a figure -- an unrun report, or two
// rows the backend refused. An axis has to have bounds before anything is
// drawn against them, and these are the only ones that claim nothing: reading
// an absent figure as 0 and deriving from it produced a 1% axis, which asserts
// a scale the run never measured.
const DEFAULT_LIMITS: MaxDropLimits = {
  maxDrop: DEFAULT_MAX_DROP,
  negativeLimit: -DEFAULT_MAX_DROP,
};

// Mirrors the stock report's rule, clamped to the magnitude the edit modal
// accepts so a derived limit is always one the user could have typed. Each
// drop is null when the row carries no figure, and a null takes no part in
// the derivation rather than standing in it as a 0.
const deriveLimits = (
  stockDrop: number | null,
  oeaDrop: number | null,
): MaxDropLimits => {
  const drops = [stockDrop, oeaDrop].filter(
    (drop): drop is number => drop !== null,
  );
  if (drops.length === 0) return DEFAULT_LIMITS;

  if (drops.every((drop) => drop < 0)) {
    const deepest = Math.floor(Math.min(...drops));
    return { maxDrop: DEFAULT_MAX_DROP, negativeLimit: Math.min(deepest, -1) };
  }

  const widest = Math.ceil(Math.max(...drops));
  return { maxDrop: Math.max(widest, 1), negativeLimit: -DEFAULT_MAX_DROP };
};

/** `symbol` is "" until a report has been calculated — a slot of its own. */
export const useMaxDropLimits = (
  symbol: string,
  stockDrop: number | null,
  oeaDrop: number | null,
): [MaxDropLimits, (limits: MaxDropLimits) => void] => {
  const [storedValue, setStoredValue] = useStrategyReportValue(
    MAX_DROP_LIMITS_KEY,
    "{}",
  );
  const saved: Record<string, MaxDropLimits> = JSON.parse(String(storedValue));

  // Both limits are written together — two setters would each save the entry
  // from their own closure and the second would drop the first.
  const setLimits = (limits: MaxDropLimits) =>
    setStoredValue(JSON.stringify({ ...saved, [symbol]: limits }));

  return [saved[symbol] ?? deriveLimits(stockDrop, oeaDrop), setLimits];
};
